// AuthContext.jsx
// Provides authentication state, role information, and helper functions
// (login, signup, logout) to the entire component tree via React Context API.
//
// Role system:
//   • Every new user gets role = 'user' by default.
//   • If the signup email matches REACT_APP_ADMIN_EMAIL, role = 'admin'.
//   • The role is stored in the Firestore 'users' collection.
//   • To manually promote someone to admin, set their `role` field to "admin"
//     in the Firebase Console (Firestore > users > <uid> > role).

import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase/config";

const AuthContext = createContext();

// Custom hook – call useAuth() in any component
export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser]   = useState(null);
  const [userProfile, setUserProfile]   = useState(null);
  const [userRole, setUserRole]         = useState(null);   // 'admin' | 'user' | null
  const [loading, setLoading]           = useState(true);   // true until Firebase resolves session

  // ── Derived flag ───────────────────────────────────────────────────────────
  const isAdmin = userRole === "admin";

  // ── Sign Up ────────────────────────────────────────────────────────────────
  // Automatically assigns 'admin' role if email matches the env-configured admin email.
  async function signup(email, password, displayName) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Set display name in Firebase Auth profile
    await updateProfile(userCredential.user, { displayName });

    // Determine role: admin email gets 'admin', everyone else gets 'user'
    const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || "";
    const role = email.toLowerCase() === adminEmail.toLowerCase() ? "admin" : "user";

    // Save user profile + role to Firestore 'users' collection
    await setDoc(doc(db, "users", userCredential.user.uid), {
      uid:         userCredential.user.uid,
      email,
      displayName,
      photoURL:    userCredential.user.photoURL || "",
      role,                    // 'admin' | 'user'
      createdAt:   serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      address:     "",
      addresses:   [],
      phone:       "",
      isActive:    true,
    });

    return userCredential;
  }

  // ── Login ──────────────────────────────────────────────────────────────────
  // Returns { credential, role } so the caller can navigate without waiting
  // for onAuthStateChanged to propagate through React state.
  async function login(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;

    // Fetch role immediately — don't wait on onAuthStateChanged
    const docSnap = await getDoc(doc(db, "users", uid));
    const data = docSnap.exists() ? docSnap.data() : {};

    if (data.isDisabled) {
      await signOut(auth);
      const err = new Error("Account disabled");
      err.code = "custom/account-disabled";
      throw err;
    }

    const role = data.role || "user";

    // Fire-and-forget lastLoginAt update (don't block login on this)
    setDoc(doc(db, "users", uid), { lastLoginAt: serverTimestamp() }, { merge: true });

    return { credential: userCredential, role };
  }

  // ── Logout ─────────────────────────────────────────────────────────────────
  function logout() {
    setUserRole(null);
    setUserProfile(null);
    return signOut(auth);
  }

  // ── Fetch user profile + role from Firestore ───────────────────────────────
  async function fetchUserProfile(uid) {
    try {
      const docSnap = await getDoc(doc(db, "users", uid));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserProfile(data);
        // Fall back to 'user' if the role field is somehow missing
        setUserRole(data.role || "user");
      } else {
        // Document missing (e.g. user created before role system) – default to 'user'
        setUserRole("user");
      }
    } catch {
      setUserRole("user");
    }
  }

  // ── Update User Document ───────────────────────────────────────────────────
  async function updateUserDocument(updates) {
    if (!currentUser) return;
    await setDoc(doc(db, "users", currentUser.uid), updates, { merge: true });
    // Re-fetch to update local state
    await fetchUserProfile(currentUser.uid);
  }

  // Listen for auth state changes (login / logout / page refresh)
  useEffect(() => {
    let profileUnsub = null;

    const authUnsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        profileUnsub = onSnapshot(doc(db, "users", user.uid), async (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.isDisabled) {
              await signOut(auth);
              setUserProfile(null);
              setUserRole(null);
            } else {
              setUserProfile(data);
              setUserRole(data.role || "user");
            }
          } else {
            setUserRole("user");
          }
          setLoading(false);
        }, (error) => {
          console.error("Profile sync error", error);
          setUserRole("user");
          setLoading(false);
        });
      } else {
        if (profileUnsub) profileUnsub();
        setUserProfile(null);
        setUserRole(null);
        setLoading(false);
      }
    });

    return () => {
      authUnsub();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    userRole,    // 'admin' | 'user' | null
    isAdmin,     // boolean shortcut
    signup,
    login,
    logout,
    updateUserDocument,
  };

  // Don't render children until Firebase has resolved the auth state
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
